import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "../../styles/AITraining.css"; 
// import LoadingIndicator from "../../components/LoadingIndicator"; // Currently unused
// import NnAnim2 from '../../public/nnanim2.svg'; 
// AWS API Gateway WebSocket endpoint
const WEBSOCKET_URL = "wss://7fuet5fuyl.execute-api.us-east-1.amazonaws.com/prod";

const AITraining = () => {
  const navigate = useNavigate();
  const loadFromLocalStorage = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Failed to load ${key} from localStorage:`, error);
      return defaultValue;
    }
  };
    // Model training data states
    const [annData, setAnnData] = useState(loadFromLocalStorage('ann-model-data', null));
    const [cnnData, setCnnData] = useState(loadFromLocalStorage('cnn-model-data', null));
    const [transformerData, setTransformerData] = useState(loadFromLocalStorage('transformer-model-data', null));
  
    const [shouldReconnect, setShouldReconnect] = useState(true);
    const trainingCompleteRef = useRef(false);
    const lastDataReceivedRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const statusUpdateTimeoutRef = useRef(null); // Added to manage status update debounce

    // Loading state for WebSocket connection
const [loading, setLoading] = useState(true);
// Ref for connection timeout
const connectionTimeoutRef = useRef(null);
  // Connection states
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [dataStatus, setDataStatus] = useState("waiting"); // waiting, active, inactive, completed
  const [error, setError] = useState(null);
  
  // Add ref to track reconnection timeout
  const reconnectTimeoutRef = useRef(null);
  
// Message and logs states
const [messages, setMessages] = useState(loadFromLocalStorage('ai-training-messages', []));
const [logs, setLogs] = useState(loadFromLocalStorage('ai-training-logs', []));
const [evaluationData, setEvaluationData] = useState(loadFromLocalStorage('evaluation-data', {
  ANN: null,
  CNN: null,
  Transformer: null
}));
// Define model progress tracking
const [modelProgress, setModelProgress] = useState(loadFromLocalStorage('model-progress', {
  ANN: { current: 0, max: null, complete: false },
  CNN: { current: 0, max: null, complete: false },
  Transformer: { current: 0, max: null, complete: false }
}));


const handleModelClick = (modelName, modelData) => {
  console.log(`Clicked ${modelName} with data:`, modelData); // Debug log
  navigate(`/topics/${modelName.toLowerCase()}`, { state: { modelData } });
};

  const updateConnectionStatus = useCallback((status) => {
    if (statusUpdateTimeoutRef.current) {
      clearTimeout(statusUpdateTimeoutRef.current);
    }
  
    // Increase debounce to 1000ms to reduce rapid toggling
    if (status !== connectionStatus) {
      statusUpdateTimeoutRef.current = setTimeout(() => {
        setConnectionStatus(status);
      }, 1000); // Increased from 300ms to 1000ms
    }
  }, [connectionStatus]);

  // Improved logging function with importance flag
  const logMessage = useCallback((message, type = "info", isImportant = false) => {
    const timestamp = new Date().toISOString().slice(11, 19);
    const logEntry = `${timestamp} - ${message}`;
    
    // Always log to console
    if (type === "error") {
      console.error(logEntry);
    } else if (type === "warning") {
      console.warn(logEntry);
    } else {
      console.log(logEntry);
    }
    
    // Only add certain types of messages to the visible logs, or if marked important
    if (isImportant || 
        message.includes("MESSAGE RECEIVED") || 
        message.includes("Data received") ||
        message.includes("evaluation data") ||
        message.includes("Training complete") ||
        (message.includes("DISCONNECTED") && disconnectCountRef.current >= MAX_DISCONNECT_COUNT)) {
      setLogs(prev => {
        const newLogs = [...prev, logEntry].slice(-20);
        // Save logs to localStorage
        localStorage.setItem('ai-training-logs', JSON.stringify(newLogs));
        return newLogs;
      });
    }
  }, []);

// Process incoming training data
const processTrainingData = useCallback((data) => {
  // Check if data has a model_name field
  const modelName = data.model_name;
  
  if (!modelName) {
    logMessage("Received data without model_name field", "warning", true);
    return;
  }
  
  // Extract metrics - handle both nested and flat structures
  const metrics = data.metrics || {};
  const timestamp = data.timestamp || Date.now();
  const epoch = data.epoch || data.payload?.epoch || 0;
  const loss = metrics.loss || data.loss || data.payload?.loss || 0;
  const mae = metrics.mae || data.mae || data.payload?.mae || 0;
  
  // Update model progress
  setModelProgress(prev => {
    // Get the current entry for this model
    const modelEntry = prev[modelName] || { current: 0, max: null, complete: false };
    
    // Update with new data
    const newEntry = {
      current: Math.max(modelEntry.current, epoch),
      max: data.max_epoch || modelEntry.max,
      complete: modelEntry.complete || false
    };
    
    const newProgress = { 
      ...prev, 
      [modelName]: newEntry 
    };
    
    // Save to localStorage
    localStorage.setItem('model-progress', JSON.stringify(newProgress));
    
    return newProgress;
  });
  
  // Formatted data object
  const modelData = {
    epoch,
    metrics: { loss, mae },
    timestamp,
    system_stats: data.system_stats || null,
    samples: data.samples || []
  };
  
  // Route to the correct model state and save to localStorage
  switch (modelName) {
    case "ANN":
      setAnnData(modelData);
      localStorage.setItem('ann-model-data', JSON.stringify(modelData));
      break;
    case "CNN":
      setCnnData(modelData);
      localStorage.setItem('cnn-model-data', JSON.stringify(modelData));
      break;
    case "Transformer":
      setTransformerData(modelData);
      localStorage.setItem('transformer-model-data', JSON.stringify(modelData));
      break;
    default:
      logMessage(`Unknown model_name: ${modelName}`, "warning", true);
  }
  
  logMessage(`Data received for model: ${modelName}`);
}, [logMessage]);

// Process evaluation data
const processEvaluationData = useCallback((data) => {
  trainingCompleteRef.current = true;
  setShouldReconnect(false);
  logMessage("Training complete -Reconnection disabled", "info", true);
  if (!data || !data.models) {
    logMessage("Received malformed evaluation data", "warning", true);
    return;
  }
  
  // Process each model's evaluation data
  Object.keys(data.models).forEach(modelName => {
    const modelData = data.models[modelName];
    
    // Update evaluation data state
    setEvaluationData(prev => {
      const newEvalData = {
        ...prev,
        [modelName]: modelData
      };
      
      // Save to localStorage
      localStorage.setItem('evaluation-data', JSON.stringify(newEvalData));
      
      return newEvalData;
    });
    
    // Mark model as complete
    setModelProgress(prev => {
      const newProgress = {
        ...prev,
        [modelName]: {
          ...prev[modelName],
          complete: true,
          evaluationTime: data.timestamp
        }
      };
      
      // Save to localStorage
      localStorage.setItem('model-progress', JSON.stringify(newProgress));
      
      return newProgress;
    });
    
    logMessage(`Training completed for model: ${modelName}`, "info", true);
    
  });
  
  // Find best model
  let bestModel = null;
  let bestMAE = Infinity;
  
  Object.keys(data.models).forEach(modelName => {
    const mae = data.models[modelName].metrics.mae;
    if (mae < bestMAE) {
      bestMAE = mae;
      bestModel = modelName;
    }
  });
  
  if (bestModel) {
    logMessage(`Best performing model: ${bestModel} with MAE of ${bestMAE.toFixed(2)}`, "info", true);
  }
  
  // Update data status
  setDataStatus("completed");
}, [logMessage]);

useEffect(() => {
  // Initialize trainingCompleteRef based on loaded modelProgress or evaluationData
  if (Object.values(modelProgress).some(model => model.complete) ||
      Object.values(evaluationData).some(data => data !== null)) {
    trainingCompleteRef.current = true;
    setShouldReconnect(false);
  }
}, [modelProgress, evaluationData]); // Added missing dependencies
  
  // Use refs to track connection state and timing
  const socketRef = useRef(null);
  const connectedRef = useRef(false);
  const pingIntervalRef = useRef(null);
  const disconnectCountRef = useRef(0);
  const dataCheckIntervalRef = useRef(null);
  const MAX_DISCONNECT_COUNT = 20;
  const DATA_TIMEOUT = 60000; // 60 seconds without data = inactive



  // Function to check data inactivity - currently unused but kept for potential future use
  // const checkDataInactivity = () => {
  //   if (lastDataReceivedRef.current && 
  //       (Date.now() - lastDataReceivedRef.current > DATA_TIMEOUT) && 
  //       socketRef.current && 
  //       socketRef.current.readyState === WebSocket.OPEN) {
    
  //     logMessage("No data received for 60 seconds - checking connection with ping", "warning", true);
    
  //     try {
  //       // socketRef.current.send(JSON.stringify({ action: "ping" }));
  //     } catch (error) {
  //       logMessage(`Failed to send ping check: ${error.message}`, "error", true);
  //       // Only force reconnection if we can't send a ping
  //       socketRef.current.close();
  //     }
  //   }
  // };

  useEffect(() => {
   // In AITraining.jsx

// 1. Update the createWebSocketConnection function
const createWebSocketConnection = () => {
  // Reset connection state
  disconnectCountRef.current = 0;
  lastDataReceivedRef.current = null;
  connectedRef.current = false;
  setShouldReconnect(true);
  setError(null);
  setConnectionStatus("Connecting");
  
  // Clear any existing timeouts
  if (connectionTimeoutRef.current) {
    clearTimeout(connectionTimeoutRef.current);
  }
  
  if (reconnectTimeoutRef.current) {
    clearTimeout(reconnectTimeoutRef.current);
  }
  
  // Set a timeout to handle connection failure
  connectionTimeoutRef.current = setTimeout(() => {
    if (!connectedRef.current) {
      logMessage("Connection timeout after 10 seconds", "error", true);
      setConnectionStatus("Disconnected");
      setError("Failed to connect to WebSocket server. Please try reconnecting.");
      setLoading(false);
    }
  }, 10000); // 10 seconds timeout
  
  // Close any existing WebSocket connection
  if (socketRef.current) {
    logMessage("Closing existing WebSocket connection...");
    try {
      socketRef.current.close(1000, "Normal closure");
    } catch (err) {
      console.error("Error closing socket:", err);
    }
  }
  
  if (pingIntervalRef.current) {
    clearInterval(pingIntervalRef.current);
    pingIntervalRef.current = null;
  }
  
  // logMessage("Creating new WebSocket connection...");
  
  try {
    socketRef.current = new WebSocket(WEBSOCKET_URL);
    const socket = socketRef.current;
    
    socket.onopen = () => {
      logMessage("WebSocket connection established", "info", true);
      updateConnectionStatus("Connected");
      setError(null);
      connectedRef.current = true;
      disconnectCountRef.current = 0;
      // logMessage("Waiting for messages...");
      setLoading(false);
      clearTimeout(connectionTimeoutRef.current);
      
      // Start ping interval - using a CONSISTENT format
      pingIntervalRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          // SIMPLE format that matches server expectations
          try {
            // socket.send(pingMessage);
            // logMessage(`🚀 SENT PING at ${new Date().toISOString()}`, "info", false);
          } catch (error) {
            logMessage(`🚨 PING ERROR: ${error.message}`, "error", true);
            // Force reconnection on send failure
            socket.close(1006, "Ping send failure");
          }
        } else {
          logMessage(`⚠️ PING SKIPPED: Connection not open (${socket.readyState})`, "warning", false);
          
          // If socket isn't open, try to reconnect
          if (socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
            createWebSocketConnection();
          }
        }
      }, 30000); // Longer interval - 30 seconds
      
      // Also start a data inactivity check
      dataCheckIntervalRef.current = setInterval(() => {
        if (lastDataReceivedRef.current && 
            Date.now() - lastDataReceivedRef.current > DATA_TIMEOUT &&
            socket.readyState === WebSocket.OPEN) {
          logMessage("No data received for extended period, checking connection", "warning", true);
          // Send a ping to check connection
          try {
            // socket.send(JSON.stringify({ action: "ping" }));
          } catch (err) {
            logMessage("Connection appears dead, forcing reconnection", "error", true);
            socket.close(1006, "Data timeout");
          }
        }
      }, DATA_TIMEOUT); // Check every minute
    };
    
    socket.onmessage = (event) => {
      disconnectCountRef.current = 0;
      lastDataReceivedRef.current = Date.now();
      setDataStatus("active");
      
      try {
        const data = JSON.parse(event.data);
        // logMessage(`Received message: ${JSON.stringify(data)}`, "info", true);
        if (data.message === "Forbidden") {
          logMessage(`Received Forbidden message: ${JSON.stringify(data)}`, "error", true);
          // Close the connection and stop all reconnection attempts
          if (socketRef.current) {
            logMessage("Closing connection due to Forbidden message and stopping reconnection", "warning", true);
            socketRef.current.close(1000, "Forbidden error");
            updateConnectionStatus("Disconnected");
            setDataStatus("inactive");
            setShouldReconnect(false); // Disable reconnection
            setError("Received Forbidden message from server. Please reload the page or try again later.");
            // Clear all timers to prevent further activity
            if (dataCheckIntervalRef.current) {
              clearInterval(dataCheckIntervalRef.current);
              dataCheckIntervalRef.current = null;
            }
            if (inactivityTimeoutRef.current) {
              clearInterval(inactivityTimeoutRef.current);
              inactivityTimeoutRef.current = null;
            }
          }
          return;
        }
        
        // Process different message types
        if (data.action === "pong") {
          logMessage(`🎉 RECEIVED PONG at ${new Date().toISOString()}`, "info", false);
          // No need for drastic action on pong - connection is working
        } else if (data.message_type === "evaluation") {
          logMessage(`Received evaluation data - training completed`, "info", true);
          processEvaluationData(data);
        } else if (data.model_name) {
          logMessage(`Received training update`);
          setMessages(prev => {
            const newMessages = [data, ...prev].slice(0, 16);
            localStorage.setItem('ai-training-messages', JSON.stringify(newMessages));
            return newMessages;
          });     
          processTrainingData(data);
        }
      } catch (error) {
        logMessage(`Failed to parse message: ${error.message}`, "error", true);
      }
      
      // Only close connection if training is complete - NOT on every message!
      if (trainingCompleteRef.current) {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (dataCheckIntervalRef.current) {
          clearInterval(dataCheckIntervalRef.current);
          dataCheckIntervalRef.current = null;
        }
        if (socket.readyState === WebSocket.OPEN) {
          socket.close(1000, "Training complete");
          logMessage("WebSocket closed due to training completion", "info", true);
          updateConnectionStatus("Disconnected");
        }
      }
    };
    
    socket.onerror = (error) => {
      logMessage(`❌ CONNECTION ERROR occurred`, "error", true);
      updateConnectionStatus("Error");
      setError(`WebSocket error occurred. Check browser console for details.`);
      connectedRef.current = false;
      setLoading(false);
      clearTimeout(connectionTimeoutRef.current);
    };
    
    socket.onclose = (event) => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (dataCheckIntervalRef.current) {
        clearInterval(dataCheckIntervalRef.current);
        dataCheckIntervalRef.current = null;
      }
      
      disconnectCountRef.current += 1;
      connectedRef.current = false;
      
      // Log based on close code
      if (event.code === 1000) {
        logMessage(`WebSocket closed normally`, "info", true);
      } else {
        logMessage(`WebSocket connection closed (code: ${event.code})`, "warning", true);
      }
      
      if (shouldReconnect && !trainingCompleteRef.current && event.code !== 1000) {
        // Exponential backoff for reconnection
        const delay = Math.min(1000 * Math.pow(1.5, Math.min(disconnectCountRef.current, 10)), 30000);
        logMessage(`Scheduling reconnect in ${(delay / 1000).toFixed(1)} seconds...`, "info", true);
        
        // Only reconnect if we haven't exceeded max attempts
        if (disconnectCountRef.current < MAX_DISCONNECT_COUNT) {
          reconnectTimeoutRef.current = setTimeout(createWebSocketConnection, delay);
        } else {
          logMessage(`Maximum reconnection attempts (${MAX_DISCONNECT_COUNT}) reached. Giving up.`, "error", true);
          updateConnectionStatus("Disconnected");
          setError("Failed to maintain WebSocket connection after multiple attempts. Please reload the page or try again later.");
        }
      } else {
        if (trainingCompleteRef.current) {
          updateConnectionStatus("Training Complete");
        } else {
          updateConnectionStatus("Disconnected");
        }
      }
    };
  } catch (err) {
    logMessage(`Failed to create WebSocket: ${err.message}`, "error", true);
    updateConnectionStatus("Error");
    setError(`Failed to create WebSocket: ${err.message}`);
    setLoading(false);
    clearTimeout(connectionTimeoutRef.current);
  }
};
  
    createWebSocketConnection();
  
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      if (dataCheckIntervalRef.current) {
        clearInterval(dataCheckIntervalRef.current);
      }
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
  
      if (statusUpdateTimeoutRef.current) {
        clearTimeout(statusUpdateTimeoutRef.current);
      }
  
      if (socketRef.current) {
        logMessage("Closing WebSocket connection due to component unmount");
        socketRef.current.close();
      }
    };
  }, [shouldReconnect, logMessage, updateConnectionStatus, processEvaluationData, processTrainingData]); // Added missing dependencies


  // Add this new useEffect for inactivity check
useEffect(() => {
  // Function to check for inactivity
  const checkInactivity = () => {
    if (lastDataReceivedRef.current) {
      const inactivityDuration = Date.now() - lastDataReceivedRef.current;
      
      // If no data for 10 minutes, stop the connection
      if (inactivityDuration > 10 * 60 * 1000 && shouldReconnect) {
        logMessage("No data received for 10 minutes - disabling automatic reconnection", "info", true);
        setShouldReconnect(false);
        
        // Update status to indicate inactivity
        setDataStatus("inactive");
      }
    }
  };
  
  // Check for inactivity every minute
  inactivityTimeoutRef.current = setInterval(checkInactivity, 60000);
  
  // Clean up on unmount
  return () => {
    if (inactivityTimeoutRef.current) {
      clearInterval(inactivityTimeoutRef.current);
    }
  };
}, [shouldReconnect, logMessage]);

// Function to manually trigger reconnection - currently unused but kept for potential future use
// const handleReconnect = () => {
//   logMessage("Manual reconnection triggered", "info", true);
//   disconnectCountRef.current = 0;
//   lastDataReceivedRef.current = null;
//   connectedRef.current = false;
//   setShouldReconnect(true);
//   setError(null);
//   setConnectionStatus("Connecting");
//   setLoading(true); // Start loading

//   if (connectionTimeoutRef.current) {
//     clearTimeout(connectionTimeoutRef.current);
//   }

//   connectionTimeoutRef.current = setTimeout(() => {
//     if (!connectedRef.current) {
//       logMessage("Connection timeout after 10 seconds", "error", true);
//       setConnectionStatus("Disconnected");
//       setError("Failed to connect to WebSocket server. Please try reconnecting.");
//       setLoading(false); // Stop loading on timeout
//     }
//   }, 10000);

//   if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
//     socketRef.current.close();
//   }
//   if (pingIntervalRef.current) {
//     clearInterval(pingIntervalRef.current);
//   }

//   setTimeout(() => {
//     try {
//       logMessage("Creating new WebSocket connection after manual reconnect");
//       socketRef.current = new WebSocket(WEBSOCKET_URL);
      
//       const socket = socketRef.current;
      
//       socket.onopen = () => {
//         logMessage("RECONNECTED: WebSocket connection re-established", "info", true);
//         updateConnectionStatus("Connected");
//         connectedRef.current = true;
//         logMessage("Waiting for messages after reconnection...");
//         setLoading(false); // Stop loading on success
//         clearTimeout(connectionTimeoutRef.current);

//         pingIntervalRef.current = setInterval(() => {
//           if (socket.readyState === WebSocket.OPEN) {
//             // socket.send(JSON.stringify({ action: "ping" }));
//             // socket.send(JSON.stringify({ routeKey: "sendmessage", action: "ping" }));

//           } else {
//             clearInterval(pingIntervalRef.current);
//           }
//         }, 30000);
//       };
      
//       socket.onerror = (err) => {
//         logMessage("❌ Reconnection failed", "error", true);
//         setConnectionStatus("Error");
//         setError("Reconnection failed");
//         connectedRef.current = false;
//         console.error("Reconnection error:", err);
//         setLoading(false); // Stop loading on error
//         clearTimeout(connectionTimeoutRef.current);
//       };
      
//       socket.onmessage = (event) => {
//         disconnectCountRef.current = 0;
//         lastDataReceivedRef.current = Date.now();

//         try {
//           const data = JSON.parse(event.data);
//           console.log("Raw WebSocket message received (reconnect):", event.data);

//           if (data.action === "pong") {
//             console.log("Received pong from server after reconnect");
//           } else if (data.message_type === "evaluation") {
//             logMessage(`Received evaluation data after reconnect`, "info", true);
//             processEvaluationData(data);
//           } else if (data.model_name) {
//             logMessage(`Received training update after reconnect`);
//             setMessages(prev => {
//               const newMessages = [data, ...prev].slice(0, 16);
//               localStorage.setItem('ai-training-messages', JSON.stringify(newMessages));
//               return newMessages;
//             });
//             processTrainingData(data);
//           } else {
//             console.log("Ignoring non-training message (reconnect):", event.data);
//           }
//         } catch (error) {
//           logMessage(`Failed to parse message: ${error.message}`, "error", true);
//         }

//         if (trainingCompleteRef.current || (lastDataReceivedRef.current && 
//             (Date.now() - lastDataReceivedRef.current > 10 * 60 * 1000))) {
//           if (pingIntervalRef.current) {
//             clearInterval(pingIntervalRef.current);
//             pingIntervalRef.current = null;
//           }
//           if (socket.readyState === WebSocket.OPEN) {
//             socket.close();
//             logMessage("WebSocket closed due to training completion or 10-minute inactivity", "info", true);
//             updateConnectionStatus("Disconnected");
//             setLoading(false);
//           }
//         }
//       };
      
//       socket.onclose = (event) => {
//         if (pingIntervalRef.current) {
//           clearInterval(pingIntervalRef.current);
//         }

//         if (shouldReconnect && !trainingCompleteRef.current) {
//           disconnectCountRef.current += 1;

//           if (disconnectCountRef.current >= MAX_DISCONNECT_COUNT) {
//             logMessage(`DISCONNECTED: WebSocket connection closed (code: ${event.code}) after ${disconnectCountRef.current} attempts`, 
//                     "warning", true);
//             setConnectionStatus("Disconnected");
//             setLoading(false);
//           }
//         } else {
//           logMessage("Connection closed - not reconnecting because training is complete or inactive", "info", true);
//           setConnectionStatus("Training Complete");
//           setLoading(false);
//         }

//         connectedRef.current = false;
//       };
      
//     } catch (err) {
//       logMessage(`Failed to create WebSocket: ${err.message}`, "error", true);
//       setError(`Failed to create WebSocket: ${err.message}`);
//       setLoading(false);
//       clearTimeout(connectionTimeoutRef.current);
//     }
//   }, 1000);
// };

  // System Stats Card Component
  const SystemStatsCard = ({ data }) => {
    if (!data || !data.system_stats) return null;
    
    const { cpu, gpu } = data.system_stats;
    
    return (
      <div className="p-4 shadow-md rounded-lg mb-4" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
        <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>System Stats</h2>
        
        <div className="mb-3">
          <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>CPU</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>Usage: {cpu['CPU Usage (%)']}%</div>
            <div>Memory: {(cpu['Memory Used (MB)'] / 1024).toFixed(1)}GB / {(cpu['Memory Total (MB)'] / 1024).toFixed(1)}GB</div>
          </div>
        </div>
        
        {gpu && gpu.length > 0 && (
          <div>
            <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>GPU</h3>
            {gpu.map((g, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <div>Usage: {g['Usage (%)']}%</div>
                <div>Memory: {(g['Memory Used (MB)'] / 1024).toFixed(1)}GB / {(g['Memory Total (MB)'] / 1024).toFixed(1)}GB</div>
                <div className="col-span-2">
                  Temperature: <span className={g['Temperature (°C)'] > 80 ? 'text-red-500' : g['Temperature (°C)'] > 70 ? 'text-yellow-500' : 'text-green-500'}>
                    {g['Temperature (°C)']}°C
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Evaluation Results Component for individual models
const EvaluationResults = ({ modelName }) => {
  if (!evaluationData[modelName]) return null;
  
  const data = evaluationData[modelName];
  
  return (
    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
      <div className="flex items-center mb-2">
        <span className="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
          Training Complete
        </span>
        <h3 className="text-md font-semibold">Final Evaluation</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><strong>MSE:</strong> {data.metrics.mse.toFixed(4)}</div>
        <div><strong>MAE:</strong> {data.metrics.mae.toFixed(4)}</div>
      </div>
      
      {data.samples && data.samples.length > 0 && (
        <>
          <h4 className="text-sm font-medium mt-2 mb-1">Sample Predictions</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs">
                <tr>
                  <th className="px-2 py-1">Prediction</th>
                  <th className="px-2 py-1">Actual</th>
                  <th className="px-2 py-1">Error</th>
                </tr>
              </thead>
              <tbody>
                {data.samples.map((sample, i) => (
                  <tr key={i} className="bg-white border-b">
                    <td className="px-2 py-1">{sample.prediction.toFixed(2)}</td>
                    <td className="px-2 py-1">{sample.true_value.toFixed(2)}</td>
                    <td className={`px-2 py-1 ${Math.abs(sample.prediction - sample.true_value) > 20 ? 'text-red-500' : 'text-green-500'}`}>
                      {(sample.prediction - sample.true_value).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Model Comparison Component
const ModelComparison = () => {
  // Only show if we have evaluation data for at least one model
  if (!evaluationData || Object.values(evaluationData).every(v => v === null)) {
    return null;
  }
  
  // Extract metrics from all completed models
  const modelMetrics = {};
  let bestModelName = null;
  let bestMAE = Infinity;
  
  Object.keys(evaluationData).forEach(modelName => {
    if (evaluationData[modelName] && evaluationData[modelName].metrics) {
      const mae = evaluationData[modelName].metrics.mae || Infinity;
      const mse = evaluationData[modelName].metrics.mse || 0;
      
      modelMetrics[modelName] = { mae, mse };
      
      // Track the best model (lowest MAE)
      if (mae < bestMAE) {
        bestMAE = mae;
        bestModelName = modelName;
      }
    }
  });
  
  // Only proceed if we have metrics
  if (Object.keys(modelMetrics).length === 0) {
    return null;
  }
  
  return (
    <div className="mt-8 p-6 shadow-md rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
      
      <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Model Comparison</h2>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="px-6 py-3">Model</th>
              <th className="px-6 py-3">MAE</th>
              <th className="px-6 py-3">MSE</th>
              <th className="px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(modelMetrics).map(modelName => (
              <tr key={modelName} className={`bg-white border-b ${modelName === bestModelName ? 'bg-green-50' : ''}`}>
                <td className="px-6 py-4 font-medium">{modelName}</td>
                <td className="px-6 py-4">{modelMetrics[modelName].mae.toFixed(2)}</td>
                <td className="px-6 py-4">{modelMetrics[modelName].mse.toFixed(2)}</td>
                <td className="px-6 py-4">
                  {modelName === bestModelName ? (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      Best Model
                    </span>
                  ) : "Complete"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6">
        <h3 className="text-md font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Performance Visualization</h3>
        <div className="h-20 flex items-end space-x-2">
          {Object.keys(modelMetrics).map(modelName => {
            // Calculate relative height (inverted - lower MAE = higher bar)
            const maxMAE = Math.max(...Object.values(modelMetrics).map(m => m.mae));
            const height = Math.max(10, 80 - ((modelMetrics[modelName].mae / maxMAE) * 80));
            
            return (
              <div key={modelName} className="flex flex-col items-center">
                <div 
                  className={`w-16 ${modelName === bestModelName ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ height: `${height}%` }}
                ></div>
                <div className="text-xs mt-1 w-16 text-center">{modelName}</div>
                <div className="text-xs text-gray-500">{modelMetrics[modelName].mae.toFixed(1)}</div>
              </div>
            );
          })}
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>Lower MAE (Mean Absolute Error) indicates better performance</p>
      </div>
      
      {bestModelName && (
        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <p className="font-medium">Summary</p>
          <p>
            The {bestModelName} model achieved the best performance with a MAE of {bestMAE.toFixed(2)}.
            {bestModelName === "Transformer" && " Transformer models often excel at capturing complex patterns in time series data."}
            {bestModelName === "ANN" && " Simple ANN models can sometimes outperform more complex architectures on certain datasets."}
            {bestModelName === "CNN" && " CNN models are particularly effective at capturing spatial patterns in the data."}
          </p>
        </div>
      )}
    </div>
  );
};

return (
  <>

    <Helmet>
      <title>AI Training Live Updates - limkc.com</title>
      <meta name="description" content="Live AI training updates with ANN, CNN, and Transformer models." />
    </Helmet>

    <div className="w-full h-full overflow-auto">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-6" style={{ backgroundColor: 'transparent', color: 'var(--text-primary)', paddingTop: 'calc(var(--navbar-height) + 1.5rem)' }}>
    {/* <img src="/nnanim2.svg" alt="Neural Network Animation" className="w-full max-w-xl mx-auto" /> */}
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-4 border-black-500 mb-4"></div>
            <p style={{ color: 'var(--text-secondary)' }}>Connecting to WebSocket server...</p>
          </div>
        </div>
      ) : (
        <>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>AI Training Live Updates</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>Live AI model training metrics updated in real-time.</p>
          { /* Add this before the status div */ }

          {/* Connection and Data status */}
          <div className="mt-4 mb-4">
            <div className="flex items-center gap-2">
              <p className="text-sm flex items-center">
                Status: 
                <span 
                  className={`led-circle ${connectionStatus === 'Connected' ? 'bg-green-600 blink' : 
                                           connectionStatus === 'Connecting' ? 'bg-yellow-600 blink' : 
                                           'bg-red-600'}`}
                ></span>
              </p>
            
            </div>

            {dataStatus && (
              <div className="flex items-center mt-1">
                <p className={`text-sm ${
                  dataStatus === 'active' ? 'text-green-600' : 
                  dataStatus === 'waiting' ? 'text-yellow-600' : 
                  dataStatus === 'completed' ? 'text-blue-600' :
                  'text-red-600'}`}
                >
                  Data Stream: {
                    dataStatus === 'active' ? 'Active' : 
                    dataStatus === 'waiting' ? 'Waiting for data...' : 
                    dataStatus === 'completed' ? 'Training Complete' :
                    'Inactive'
                  }
                </p>
              </div>
            )}

            {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
          </div>
          
          {/* Connection logs */}
          <div className="mb-6 p-3 rounded text-sm font-mono h-40 overflow-y-auto" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
            <h3 className="font-bold mb-2" style={{ color: 'var(--text-on-dark)' }}>Connection Logs:</h3>
            {logs.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>No logs yet...</p>
            ) : (
              logs.map((log, i) => <div key={i}>{log}</div>)
            )}
          </div>

          {/* Model cards */}
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {[
              { name: "ANN", data: annData },
              { name: "CNN", data: cnnData },
              { name: "Transformer", data: transformerData },
            ].map(({ name, data }) => (
              <div
                key={name}
                className="p-6 shadow-md rounded-lg hover:shadow-lg transition cursor-pointer"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                onClick={() => handleModelClick(name, data)}
              >
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{name} Model</h2>
                  {modelProgress[name]?.complete && (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      Complete
                    </span>
                  )}
                </div>
                
                {data ? (
                  <div>
                    <p>
                      <strong>Epoch:</strong> {data.epoch}
                    </p>
                    
                    {modelProgress[name]?.max && (
                      <div className="w-full bg-gray-200 rounded-full h-2.5 my-2">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${(data.epoch / modelProgress[name].max) * 100}%` }}
                        ></div>
                        <p className="text-xs text-gray-500 mt-1">
                          {data.epoch} / {modelProgress[name].max} epochs
                        </p>
                      </div>
                    )}
                    
                    <p>
                      <strong>Loss:</strong> {typeof data.metrics.loss === 'number' ? data.metrics.loss.toFixed(4) : data.metrics.loss}
                    </p>
                    <p>
                      <strong>MAE:</strong> {typeof data.metrics.mae === 'number' ? data.metrics.mae.toFixed(4) : data.metrics.mae}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Updated: {new Date(data.timestamp).toLocaleString()}
                    </p>
                    
                    {evaluationData[name] && <EvaluationResults modelName={name} />}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-secondary)' }}>Waiting for updates...</p>
                )}
              </div>
            ))}
          </div>
          
          {annData && <SystemStatsCard data={annData} />}
          {!annData && cnnData && <SystemStatsCard data={cnnData} />}
          {!annData && !cnnData && transformerData && <SystemStatsCard data={transformerData} />}

          <ModelComparison />

          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Recent Messages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {messages.slice(0, 16).map((msg, index) => (
                <div key={index} className="p-4 rounded shadow" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                  <p><strong>Model:</strong> {msg.model_name}</p>
                  <p><strong>Epoch:</strong> {msg.epoch || msg.payload?.epoch || "N/A"}</p>
                  <p><strong>Loss:</strong> {(msg.loss || msg.metrics?.loss || msg.payload?.loss || 0).toFixed(4)}</p>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                    {new Date(msg.timestamp || Date.now()).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  </>
);
};

export default AITraining;